import { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { useCallback, useEffect, useState } from 'react';
import { ID } from 'shared/types/id';
import { TaskCardTimeSection } from 'shared/types/orderable-section';
import { TaskCardTask } from 'shared/types/task-card-task';
import {
  handleItemBetweenSectionsMovement,
  handleItemWithinSectionMovement,
} from 'shared/utils/handle-section-item-movement';

export const useDroppableDayPartControls = ({
  initialSections,
  onDragEnd,
}: {
  initialSections: TaskCardTimeSection[];
  onDragEnd?: (
    sections: TaskCardTimeSection[],
    movedTask: TaskCardTask,
  ) => void;
}) => {
  const [currentSections, setCurrentSections] = useState(initialSections);
  const [activeTaskId, setActiveTaskId] = useState<ID>();

  useEffect(() => {
    setCurrentSections(initialSections);
  }, [initialSections]);

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => setActiveTaskId(active.id.toString()),
    [],
  );

  const handleDragOver = useCallback(
    ({ active, over }: DragOverEvent) => {
      if (!over?.id || over.id === active.id || !activeTaskId) {
        return;
      }

      setCurrentSections((oldSections) => {
        const { sections } = handleItemBetweenSectionsMovement(
          oldSections,
          active,
          over,
        );

        return sections;
      });
    },
    [activeTaskId],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setActiveTaskId(undefined);
      if (!over?.id || !activeTaskId) {
        return;
      }

      const { sections, activeItem: activeTask } =
        handleItemWithinSectionMovement<TaskCardTask, TaskCardTimeSection>(
          currentSections,
          active,
          over,
        );

      if (!activeTask) {
        return;
      }

      return onDragEnd?.(sections, activeTask);
    },
    [activeTaskId, currentSections, onDragEnd],
  );

  const handleDragCancel = useCallback(() => {
    setCurrentSections(initialSections);
    setActiveTaskId(undefined);
  }, [initialSections]);

  return {
    sections: currentSections,
    activeId: activeTaskId,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    setSections: setCurrentSections,
  };
};
